import React, { useCallback, useState } from 'react';
import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { useDefaultTravellerFieldsQuery } from '@websky/graphql';
import DocumentFieldsItem from './DocumentFieldsItem/DocumentFieldsItem';
import { useForm, useFormState } from 'react-final-form';
import { IconButton } from '../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { useIsMobile } from '../../../utils';
import { getCitizenships } from './DocumentFieldsItem/utils';
import DocumentCard from './DocumentCard/DocumentCard';
import AddDocumentModal from './AddDocumentModal/AddDocumentModal';
import SectionWrapper from '../SectionWrapper/SectionWrapper';
import { DocumentFields } from '../../consts';
const DocumentsSection = ({ traveller, documents, primaryCountryCodes }) => {
    var _a, _b;
    const { DocumentsSection: css } = useTheme('TravellerForm');
    const { t: tTraveller } = useTranslation('Traveller');
    const { t: tPassenger } = useTranslation('Passenger');
    const form = useForm();
    const { values, submitFailed } = useFormState({
        subscription: {
            values: true,
            submitFailed: true
        }
    });
    const isMobile = useIsMobile();
    const { data: documentsValidations } = useDefaultTravellerFieldsQuery();
    const documentValidators = (_b = (_a = documentsValidations === null || documentsValidations === void 0 ? void 0 : documentsValidations.DefaultTravellerProfile) === null || _a === void 0 ? void 0 : _a.documents) === null || _b === void 0 ? void 0 : _b[0];
    const [editingIndex, setEditingIndex] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('edit');
    const [documentSnapshot, setDocumentSnapshot] = useState(null);
    const removeDocumentHandler = useCallback((index) => {
        form.mutators.documentsMutator({
            type: 'remove',
            index
        });
    }, [form]);
    const clearDocumentFields = useCallback((index) => {
        DocumentFields.forEach(field => form.change(`documents[${index}].${field}`, undefined));
    }, [form]);
    const openEditModal = useCallback((index) => {
        var _a;
        const docValues = (_a = values === null || values === void 0 ? void 0 : values.documents) === null || _a === void 0 ? void 0 : _a[index];
        setDocumentSnapshot(docValues ? Object.assign({}, docValues) : null);
        setEditingIndex(index);
        setModalMode('edit');
        setIsModalOpen(true);
    }, [values]);
    const openAddModal = useCallback(() => {
        const newIndex = documents.length;
        form.mutators.documentsMutator({ type: 'add' });
        DocumentFields.forEach(field => {
            if (values === null || values === void 0 ? void 0 : values[field]) {
                form.change(`documents[${newIndex}].${field}`, values[field]);
            }
        });
        if (values === null || values === void 0 ? void 0 : values.nationality) {
            form.change(`documents[${newIndex}].nationality`, values.nationality);
        }
        setEditingIndex(newIndex);
        setModalMode('add');
        setIsModalOpen(true);
    }, [documents.length, form, values]);
    const resetModalState = useCallback(() => {
        setIsModalOpen(false);
        setEditingIndex(null);
        setModalMode('edit');
    }, []);
    const handleCloseModal = useCallback(() => {
        if (modalMode === 'add' && editingIndex !== null) {
            removeDocumentHandler(editingIndex);
        }
        else if (modalMode === 'edit' && editingIndex !== null) {
            DocumentFields.forEach(field => form.change(`documents[${editingIndex}].${field}`, documentSnapshot === null || documentSnapshot === void 0 ? void 0 : documentSnapshot[field]));
        }
        resetModalState();
    }, [editingIndex, modalMode, removeDocumentHandler, resetModalState, documentSnapshot, form]);
    const handleSaveModal = useCallback(() => {
        resetModalState();
    }, [resetModalState]);
    const addDocumentHandler = () => {
        form.mutators.documentsMutator({ type: 'add' });
    };
    const canRemoveDocument = documents.length > 1;
    const isEdit = modalMode === 'edit';
    const onClearDocument = !canRemoveDocument ? clearDocumentFields : undefined;
    const onRemoveDocument = canRemoveDocument ? removeDocumentHandler : undefined;
    const onClearDocumentMobileModal = isEdit ? clearDocumentFields : undefined;
    if (isMobile) {
        const citizenShips = getCitizenships(traveller, primaryCountryCodes);
        const getCitizenshipLabel = (code) => {
            const found = citizenShips.find(c => c.value === code);
            return found ? tPassenger(found.label) : code;
        };
        const showDocumentsError = submitFailed && documents.length === 0;
        return (React.createElement(SectionWrapper, { title: tTraveller('Documents'), error: showDocumentsError ? tTraveller('Please enter data') : undefined, headerButton: React.createElement(IconButton, { className: css.documents__add, onClick: openAddModal, type: "button" },
                React.createElement(CdnIcon, { icon: 'Action/Add' })) },
            documents.length > 0 && (React.createElement("div", { className: css.documentsSection },
                React.createElement("div", { className: css.documentsSection__fields }, documents.map((document, index) => {
                    return (React.createElement(DocumentCard, { key: index, document: document, index: index, docTypeLabel: document.docType ? tPassenger(document.docType) : '', citizenshipLabel: document.citizenship ? getCitizenshipLabel(document.citizenship) : undefined, onEdit: openEditModal, onRemove: onRemoveDocument }));
                })))),
            editingIndex !== null && (React.createElement(AddDocumentModal, { isOpen: isModalOpen, index: editingIndex, validators: documentValidators, traveller: traveller, primaryCountryCodes: primaryCountryCodes, onClose: handleCloseModal, onSave: handleSaveModal, onClear: onClearDocumentMobileModal, isEdit: isEdit }))));
    }
    const documentFieldsItems = documents.map((document, index) => {
        return (React.createElement(DocumentFieldsItem, { key: index, id: index, document: document, validators: documentValidators, traveller: traveller, primaryCountryCodes: primaryCountryCodes, onRemove: onRemoveDocument, onClear: onClearDocument }));
    });
    return (React.createElement("div", { className: css.documentsSection },
        React.createElement("div", { className: css.documentsSection__fields }, !!documents.length ? (documentFieldsItems) : (React.createElement("div", { className: css.documentsSection__emptyWarning }, tTraveller('You need to add a document')))),
        React.createElement("div", { className: css.documentsSection__controls },
            React.createElement("div", { className: css.documentsSection__addFields, onClick: addDocumentHandler },
                React.createElement(IconButton, { classes: {
                        root: css.buttonAdd
                    } },
                    React.createElement(CdnIcon, { icon: 'Action/Add' })),
                tTraveller('Add document')))));
};
export default DocumentsSection;
